import { type FormikProps } from "formik";
import React, { useMemo } from "react";

import { Grid } from "semantic-ui-react";
import styles from "./embeddedVideoForm.module.scss";

import { ValidatedForm } from "../../../../components/forms";

import { type Form } from "interfaces";
import { extractFormikProps } from "utils/formikUtils";
import { nameofFactory } from "utils/nameof";
import EndpointsToGetSasLink from "../../../../enums/endpointsToGetSasLink";
import { EmbeddedVideoSource } from "../types/models";
import { getSourceTooltipMessage, getVideoUrlTooltipMessage } from "./tooltipsHelpers";

import EmbeddedVideoSourceIcon from "components/icons/EmbeddedVideoSourceIcon";
import ImagePreview from "components/media/imagePreview/ImagePreview";
import {
  IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS,
  IMAGE_PREVIEW_PLACEHOLDER_VARIANTS,
} from "components/media/imagePreview/placeholders";

const videoSourceOptions = [
  {
    icon: <EmbeddedVideoSourceIcon className={styles.embeddedVideoSourceIcon} source={EmbeddedVideoSource.YouTube} />,
    text: "YouTube",
    value: 1,
  },
  {
    icon: <EmbeddedVideoSourceIcon className={styles.embeddedVideoSourceIcon} source={EmbeddedVideoSource.Vimeo} />,
    text: "Vimeo",
    value: 2,
  },
  {
    icon: <EmbeddedVideoSourceIcon className={styles.embeddedVideoSourceIcon} source={EmbeddedVideoSource.Wistia} />,
    text: "Wistia",
    value: 3,
  },
  {
    icon: <EmbeddedVideoSourceIcon className={styles.embeddedVideoSourceIcon} source={EmbeddedVideoSource.Vidyard} />,
    text: "Vidyard",
    value: 4,
  },
];

export interface EmbeddedVideoFormProps extends Form {
  id: number;
  isReadOnlyView: boolean;
}

export interface EmbeddedVideoFormFields {
  title: string;
  description: string;
  videoUrl: string;
  source?: EmbeddedVideoSource;
  thumbnailUrl: string;
}

export type EmbeddedVideosFormPropsAll = FormikProps<EmbeddedVideoFormFields> & EmbeddedVideoFormProps;

const EmbeddedVideoForm = (props: EmbeddedVideosFormPropsAll) => {
  const { values } = props;
  const formik = extractFormikProps<EmbeddedVideoFormFields, EmbeddedVideosFormPropsAll>(props);
  const nameof = nameofFactory<EmbeddedVideoFormFields>();
  const entityCreated = useMemo(() => props.id > 0, [props.id]);

  const onTitleBlur = (propertyName: string) => {
    const descriptionField = nameof("description");
    if (!entityCreated && !props.touched[descriptionField]) {
      props.setFieldValue(descriptionField, values.title, true);
    }

    props.onBlur(propertyName);
  };

  const renderVideoSourceDropdownTrigger = (
    source: EmbeddedVideoSource | undefined,
    options: typeof videoSourceOptions,
    placeholder: string,
  ): JSX.Element => {
    if (source) {
      const matched = options.find((opt) => opt.value === source);
      return (
        <>
          {matched?.icon}
          <span>{matched?.text}</span>
        </>
      );
    }

    return <span className="divider default text">{placeholder}</span>;
  };

  const renderFields = (): React.ReactNode => {
    return (
      <Grid stackable columns="equal" verticalAlign="bottom" className="overridden-bottom-margin">
        <Grid.Row>
          <Grid.Column className="seven wide">
            <ValidatedForm.DropdownField
              {...formik}
              label="Source"
              propertyName="source"
              value={values.source}
              options={videoSourceOptions}
              disabled={props.disabled}
              onBlur={props.onBlur}
              markAsRequired
              errorPosition="bottom"
              clearable
              defaultValue={null}
              tooltip={{
                info: getSourceTooltipMessage(),
                width: 17,
                hoverable: true,
              }}
              trigger={renderVideoSourceDropdownTrigger(values.source, videoSourceOptions, "Select Source")}
              isReadOnlyView={props.isReadOnlyView}
            />
          </Grid.Column>
          <Grid.Column className="nine wide">
            <ValidatedForm.InputField
              {...formik}
              label="Video URL"
              placeholder={"Enter Video URL"}
              propertyName={nameof("videoUrl")}
              value={values.videoUrl}
              onBlur={props.onBlur}
              disabled={props.disabled || !values.source}
              markAsRequired
              errorPosition="bottom"
              tooltip={{
                info: getVideoUrlTooltipMessage(),
                width: 17,
                hoverable: true,
              }}
              isReadOnlyView={props.isReadOnlyView}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <ImagePreview
              {...formik}
              label="Video Thumbnail"
              markAsRequired
              value={values.thumbnailUrl}
              propertyName={nameof("thumbnailUrl")}
              endpointToGetSasLink={EndpointsToGetSasLink.EmbeddedVideo}
              onImageChanged={props.onBlur}
              disabled={props.disabled}
              customImagePreview
              combinedIcon
              placeholder={IMAGE_PREVIEW_PLACEHOLDER_VARIANTS.image}
              headerPlaceholder={IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS.image}
              isReadOnlyView={props.isReadOnlyView}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <ValidatedForm.InputField
              {...formik}
              label="Title"
              placeholder={"Enter Video Title"}
              value={values.title}
              propertyName={nameof("title")}
              onBlur={onTitleBlur}
              markAsRequired
              disabled={props.disabled}
              tooltip={{
                info: "Titles will be seen by end-users.",
                width: 17,
              }}
              isReadOnlyView={props.isReadOnlyView}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <ValidatedForm.TextAreaField
              {...formik}
              label="Description"
              placeholder={"Enter Video Description"}
              value={values.description}
              propertyName={nameof("description")}
              onBlur={props.onBlur}
              disabled={props.disabled}
              markAsRequired
              tooltip={{
                info: "Descriptions will be seen by end-users.",
                width: 17,
              }}
              isReadOnlyView={props.isReadOnlyView}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  const { isLoading, disablePreventTransitionPrompt } = props;
  return (
    <ValidatedForm
      dirty={props.dirty}
      unsavedChangesPrompt={{
        title: "Exit Without Saving?",
        message:
          "Are you sure you want to exit without saving this Embedded Video? All information entered will be lost.",
      }}
      disablePreventTransitionPrompt={disablePreventTransitionPrompt}
      isLoading={isLoading}
      formWidthComputer={8}
      withoutFormik
    >
      {renderFields}
    </ValidatedForm>
  );
};

export default EmbeddedVideoForm;
